import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Car as CarIcon } from "lucide-react";
import { api } from "@/lib/api";

const HEADER_IMG =
  "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1600&q=80";

const Dealers = () => {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dealers").then((r) => setDealers(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HEADER_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Our network</p>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900">
            Partner dealerships across Massachusetts
          </h1>
          <p className="mt-4 max-w-2xl text-slate-600 leading-relaxed">
            Every listing on BeloMotors comes from one of the five trusted dealerships below.
            Visit their profiles to explore their full inventory.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-64 bg-slate-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="dealers-grid">
            {dealers.map((d) => (
              <Link
                key={d.id}
                to={`/dealers/${d.id}`}
                className={`group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 transition-all ${d.is_coming_soon ? "ring-1 ring-amber-200" : ""}`}
                data-testid={`dealer-card-${d.id}`}
              >
                <div className="relative aspect-[16/9] bg-slate-100 overflow-hidden">
                  <img
                    src={d.logo_url || HEADER_IMG}
                    alt={d.name}
                    className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${d.is_coming_soon ? "opacity-70" : ""}`}
                  />
                  {d.is_coming_soon && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-900/40" />
                      <span
                        className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 shadow-lg"
                        data-testid={`dealer-card-coming-soon-${d.id}`}
                      >
                        Coming Soon
                      </span>
                    </>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-semibold text-slate-900">{d.name}</h3>
                  <p className="mt-1 text-sm text-slate-500 inline-flex items-center gap-1">
                    <MapPin size={12} /> {d.address}, {d.city}, {d.state}
                  </p>
                  <p className="mt-1 text-sm text-slate-500 inline-flex items-center gap-1">
                    <Phone size={12} /> {d.phone}
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                      <CarIcon size={13} className="text-brand" />
                      <span className="font-semibold">{d.vehicle_count}</span> vehicles
                      {d.is_coming_soon && <span className="ml-1 text-amber-700 font-medium">· preview</span>}
                    </span>
                    <span className="text-xs font-semibold text-brand group-hover:underline">
                      View →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Dealers;
